import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import history from '../../history';
import { impersonateUser as exchangeImpersonationToken } from '../../requests/AccountRequests';
import { impersonateStoreClear } from '../../actions/AccountActions';
import { loginFromSession } from '../../actions/Actions';
import {
    impersonationExchangeErrorMessage,
    IMPERSONATION_SESSION_MARKER,
} from '../../config/Impersonation';

// Landing page for the admin-panel impersonation handoff (M7-365). RailsAdmin
// mints a signed, single-use token and redirects the browser here
// (`/impersonate?token=...`). We exchange it for an impersonated session: on
// success the backend sets the httpOnly session cookie, and we establish a
// client-side session so the SPA opens as the target; on failure
// (missing/invalid/expired/replayed token, or the feature flag off) we show a
// clear message instead of the old blank card.
//
// Establishing the session is the subtle part: the admin arrives from RailsAdmin
// with NO prior SPA login and the exchange returns NO JWT (auth is the cookie),
// so there is nothing to populate the SPA's `isLoggedIn` gate
// (`!isNil(appStore.session_id)`). We reuse the existing passwordless
// `loginFromSession` path with a non-null `session_id` MARKER and a NULL
// `session_name`: the marker flips `isLoggedIn`, the null name suppresses the
// `IMPRESARIO` query-param Bearer (so calls authenticate via the cookie), and
// `loginFromSession` then runs `refreshAccount` to load the target's data and
// surface the impersonation banner. `email` is null — `accountuser/view`
// resolves the user from the cookie session, not the param.
//
// This route is intentionally NOT a ProtectedRoute — the handoff token is the
// authority, so no prior login is required. The guard against bad input lives
// here: a missing or rejected token renders an explanatory message.

const STATUS = {
    PENDING: 'pending',
    ERROR: 'error',
};

function Impersonate(props) {
    // Actions are injected by connect() — react-redux 6 has no useDispatch hook.
    const {
        impersonateStoreClear: clearImpersonationStores,
        loginFromSession: establishImpersonatedSession,
    } = props;
    const [status, setStatus] = useState(STATUS.PENDING);
    const [message, setMessage] = useState('');

    useEffect(
        () => {
            const token = new URLSearchParams(window.location.search).get('token');

            if (!token) {
                setMessage(impersonationExchangeErrorMessage());
                setStatus(STATUS.ERROR);
                return undefined;
            }

            let cancelled = false;
            exchangeImpersonationToken(token)
                .then(() => {
                    if (cancelled) {
                        return;
                    }
                    // Drop any stale state from a prior SPA user on this browser,
                    // then establish the impersonated session (marker id + null
                    // name => cookie auth, no Bearer) which loads the target via
                    // refreshAccount, and navigate into the app as that user.
                    clearImpersonationStores();
                    establishImpersonatedSession({
                        sessionId: IMPERSONATION_SESSION_MARKER,
                        sessionName: null,
                        email: null,
                    });
                    // replace, not push, so the token-bearing /impersonate URL is
                    // not left in SPA/browser history (back-button + history exfil).
                    history.replace('/');
                })
                .catch(error => {
                    if (cancelled) {
                        return;
                    }
                    setMessage(impersonationExchangeErrorMessage(error));
                    setStatus(STATUS.ERROR);
                });

            return () => {
                cancelled = true;
            };
        },
        [clearImpersonationStores, establishImpersonatedSession]
    );

    return (
        <div className="application">
            <div className="applicationBody">
                <div className="cardContainer cardContent">
                    {status === STATUS.PENDING ? (
                        <p role="status" aria-live="polite">
                            Signing you in…
                        </p>
                    ) : (
                        <p className="impersonateMessage" role="status" aria-live="polite">
                            {message}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default connect(
    null,
    { impersonateStoreClear, loginFromSession }
)(Impersonate);
